import React, { ReactElement, useMemo } from "react";
import { makeStyles, Paper, Typography } from "@material-ui/core";
import Loader from "./Loader/Loader";
import { NonDuesRevenueBreakdownReturn } from "../../graphql/types";
import { useEnumStrings } from "../enumStrings";

type NonDuesRevenueBreakdownTableProps = {
  data?: NonDuesRevenueBreakdownReturn;
  chamberOrBoardText: string;
  isLoading?: boolean;
};

const goldColor = "#E1A731";
const redColor = "#EE1C23";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    width: "100%",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  mainTitle: {
    fontSize: "22px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: -5,
  },
  titleAmount: {
    fontSize: "28px",
    fontWeight: 700,
    color: redColor,
  },
  subtitle: {
    fontSize: "19px",
    color: "#666",
  },
  tableCard: {
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0px 0px 10px 1px lightgrey",
  },
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 800,
  },
  tableHeader: {
    backgroundColor: goldColor,
  },
  tableHeaderCell: {
    padding: theme.spacing(1.5, 2),
    textAlign: "center",
    fontSize: "18px",
    fontWeight: 600,
    color: "#FFFFFF",
    borderBottom: "none",
    "&:first-child": {
      textAlign: "left",
    },
  },
  tableRow: {
    borderBottom: "1px solid #E5E7EB",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: "18px",

    textAlign: "center",
    borderBottom: "1px solid #E5E7EB",
    "&:first-child": {
      textAlign: "left",
      fontWeight: 500,
    },
  },
  totalRow: {},
  totalCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: "18px",
    fontWeight: 600,
    textAlign: "center",
    "&:first-child": {
      textAlign: "left",
    },
  },
  currentRevenueHighlight: {
    backgroundColor: redColor,
    fontWeight: 700,
  },
  summaryText: {
    fontSize: "19px",
    lineHeight: 1.6,
    marginTop: theme.spacing(1),
  },
  boldText: {
    fontWeight: 700,
  },
  smallNote: {
    fontSize: "13px",
    color: "#666",
    display: "block",
  },
  loaderWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    boxShadow: "0px 0px 10px 1px lightgrey",
  },
}));

const NonDuesRevenueBreakdownTable = ({
  data,
  chamberOrBoardText,
  isLoading = false,
}: NonDuesRevenueBreakdownTableProps): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const strings = enumStrings.revenueBreakdown;

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `$${Math.round(amount).toLocaleString()}`;
  };

  // Calculate percentages
  const calculations = useMemo(() => {
    if (!data) {
      return {
        featuredMemberPercentage: 0,
        categoryBannerPercentage: 0,
        titleSponsorshipPercentage: 0,
        totalPercentage: 0,
        remainingRevenue: 0,
      };
    }

    const featuredMemberPercentage =
      data.featuredMemberPotentialRevenue > 0
        ? (data.featuredMemberCurrentRevenue /
            data.featuredMemberPotentialRevenue) *
          100
        : 0;

    const categoryBannerPercentage =
      data.categoryBannerPotentialRevenue > 0
        ? (data.categoryBannerCurrentRevenue /
            data.categoryBannerPotentialRevenue) *
          100
        : 0;

    const titleSponsorshipPercentage = data.hasTitleSponsor ? 100 : 0;

    const totalPercentage =
      data.totalPotentialRevenue > 0
        ? (data.totalCurrentRevenue / data.totalPotentialRevenue) * 100
        : 0;

    const remainingRevenue =
      data.featuredMemberPotentialRevenue -
      data.featuredMemberCurrentRevenue +
      (data.categoryBannerPotentialRevenue - data.categoryBannerCurrentRevenue);

    return {
      featuredMemberPercentage,
      categoryBannerPercentage,
      titleSponsorshipPercentage,
      totalPercentage,
      remainingRevenue,
    };
  }, [data]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.loaderWrapper}>
          <Loader />
        </div>
      </div>
    );
  }

  // Show empty state if no data
  if (!data) {
    return (
      <div className={classes.container}>
        <div className={classes.loaderWrapper}>
          <Typography>{strings.noDataAvailable}</Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.mainTitle}>
          <span>{strings.mainTitle}</span>
          <span className={classes.titleAmount}>
            {formatCurrency(data.totalCurrentRevenue)}
          </span>
        </div>
        <Typography className={classes.subtitle}>
          {strings.subtitle.replace(
            "{{chamberOrBoard}}",
            chamberOrBoardText.toLowerCase()
          )}
        </Typography>
      </div>

      {/* Table */}
      <Paper className={classes.tableCard} elevation={0}>
        <div className={classes.tableWrapper}>
          <table className={classes.table}>
            <thead>
              <tr className={classes.tableHeader}>
                <th className={classes.tableHeaderCell}>
                  {strings.tableHeaders.category}
                </th>
                <th className={classes.tableHeaderCell}>
                  {strings.tableHeaders.sold}
                </th>
                <th className={classes.tableHeaderCell}>
                  {strings.tableHeaders.revenuePerBanner}
                </th>
                <th className={classes.tableHeaderCell}>
                  {strings.tableHeaders.currentRevenue}
                </th>
                <th className={classes.tableHeaderCell}>
                  {strings.tableHeaders.potentialRevenue}
                </th>
                <th className={classes.tableHeaderCell}>
                  {strings.tableHeaders.percentAchieved}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Featured Member Banners Row */}
              <tr className={classes.tableRow}>
                <td className={classes.tableCell}>
                  {strings.featuredMemberBanners} (
                  {formatCurrency(data.featuredMemberBannerPrice)})
                </td>
                <td className={classes.tableCell}>
                  {data.featuredMemberBannersSold}/
                  {data.featuredMemberBannersTotal}
                </td>
                <td className={classes.tableCell}>
                  {formatCurrency(data.featuredMemberRevenuePerBanner)}
                </td>
                <td className={classes.tableCell}>
                  {formatCurrency(data.featuredMemberCurrentRevenue)}
                </td>
                <td className={classes.tableCell}>
                  {formatCurrency(data.featuredMemberPotentialRevenue)}
                  <span className={classes.smallNote}>
                    {strings.potentialRevenueBasedOnPercentage}
                  </span>
                </td>
                <td className={classes.tableCell}>
                  {calculations.featuredMemberPercentage.toFixed(1)}%
                </td>
              </tr>

              {/* Category Banner Sales Row */}
              <tr className={classes.tableRow}>
                <td className={classes.tableCell}>
                  {strings.categoryBannerSales} (
                  {formatCurrency(data.categoryBannerPrice)})
                </td>
                <td className={classes.tableCell}>
                  {data.categoryBannersSold}/{data.categoryBannersTotal}
                </td>
                <td className={classes.tableCell}>
                  {formatCurrency(data.categoryBannerRevenuePerBanner)}
                </td>
                <td className={classes.tableCell}>
                  {formatCurrency(data.categoryBannerCurrentRevenue)}
                </td>
                <td className={classes.tableCell}>
                  {formatCurrency(data.categoryBannerPotentialRevenue)}
                </td>
                <td className={classes.tableCell}>
                  {calculations.categoryBannerPercentage.toFixed(0)}%
                </td>
              </tr>

              {/* Title Sponsorship Row */}
              <tr className={classes.tableRow}>
                <td className={classes.tableCell}>
                  {strings.titleSponsorshipSale} (
                  {formatCurrency(data.titleSponsorshipAmount)}{" "}
                  <span style={{ display: "inline", fontSize: "13px" }}>
                    {strings.estimatedRecommendation}
                  </span>
                  )
                </td>
                <td className={classes.tableCell}>
                  {data.hasTitleSponsor ? "1" : "0"}/1
                </td>
                <td className={classes.tableCell}>
                  {formatCurrency(data.titleSponsorshipAmount)}*
                  <span className={classes.smallNote}>
                    {strings.estimatedRecommendedAmount}
                  </span>
                </td>
                <td className={classes.tableCell}>
                  {formatCurrency(
                    data.hasTitleSponsor ? data.titleSponsorshipAmount : 0
                  )}
                  *
                  <span className={classes.smallNote}>
                    {strings.estimatedRecommendedAmount}
                  </span>
                </td>
                <td className={classes.tableCell}>
                  {formatCurrency(data.titleSponsorshipAmount)}*
                  <span className={classes.smallNote}>
                    {strings.priceBasedOnMembers}
                  </span>
                </td>
                <td className={classes.tableCell}>
                  {calculations.titleSponsorshipPercentage}%
                </td>
              </tr>

              {/* Total Row */}
              <tr className={classes.totalRow}>
                <td
                  style={{ backgroundColor: goldColor, color: "#FFFFFF" }}
                  className={classes.totalCell}
                >
                  {strings.total}
                </td>
                <td className={classes.totalCell}>-</td>
                <td className={classes.totalCell}>-</td>
                <td
                  style={{ backgroundColor: redColor, color: "#FFFFFF" }}
                  className={classes.totalCell}
                >
                  {formatCurrency(data.totalCurrentRevenue)}
                </td>
                <td className={classes.totalCell}>
                  {formatCurrency(data.totalPotentialRevenue)}
                </td>
                <td className={classes.totalCell}>
                  ~{calculations.totalPercentage.toFixed(1)}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Summary Text */}
      <Typography className={classes.summaryText}>
        {strings.summaryPart1.replace(
          "{{chamberOrBoard}}",
          chamberOrBoardText.toLowerCase()
        )}{" "}
        <span className={classes.boldText}>
          {formatCurrency(data.totalCurrentRevenue)}
        </span>{" "}
        {strings.summaryPart2.replace(
          "{{percentage}}",
          `${calculations.totalPercentage.toFixed(1)}%`
        )}{" "}
        <span className={classes.boldText}>
          {strings.featuredMemberBanners}
        </span>{" "}
        {strings.and}{" "}
        <span className={classes.boldText}>{strings.categoryBanners}</span>{" "}
        {strings.summaryPart3}{" "}
        <span className={classes.boldText}>
          {formatCurrency(calculations.remainingRevenue)}
        </span>{" "}
        {strings.summaryPart4}
      </Typography>
    </div>
  );
};

export default NonDuesRevenueBreakdownTable;
